import { BookOpen, ListChecks, BookMarked, FileText, ClipboardCheck, Landmark, Presentation, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, PageHero } from "@/components/shared";

const resourceTypes = [
  { icon: BookOpen, title: "Guides pratiques", text: "Des repères clairs pour aborder une transformation sans jargon ni précipitation.", state: "En préparation" },
  { icon: ListChecks, title: "Listes de contrôle", text: "Des points de vérification simples pour préparer une décision ou une adaptation.", state: "En préparation" },
  { icon: BookMarked, title: "Glossaires", text: "Des définitions accessibles pour comprendre les termes qui reviennent le plus souvent.", state: "En préparation" },
  { icon: FileText, title: "Fiches de compréhension", text: "Des synthèses courtes pour saisir l'essentiel d'un sujet en quelques minutes.", state: "En préparation" },
  { icon: ClipboardCheck, title: "Outils d'auto-évaluation", text: "Des grilles de questionnement pour situer sa propre organisation face à une évolution.", state: "En préparation" },
  { icon: Presentation, title: "Comptes rendus", text: "Des restitutions de conférences et de réunions, publiées lorsqu'elles auront lieu.", state: "À venir" },
];

const officialLinks = [
  { label: "France Num — accompagnement numérique des TPE-PME", url: "https://www.francenum.gouv.fr/" },
  { label: "CNIL — protection des données personnelles", url: "https://www.cnil.fr/" },
  { label: "ANSSI — cybersécurité", url: "https://cyber.gouv.fr/" },
  { label: "economie.gouv.fr — informations aux entreprises", url: "https://www.economie.gouv.fr/" },
  { label: "service-public.fr — démarches des entreprises", url: "https://entreprendre.service-public.fr/" },
];

export default function Ressources() {
  return (
    <>
      <Seo
        title="Ressources — BENZENATI"
        description="Guides pratiques, listes de contrôle, glossaires, fiches de compréhension et liens vers les organismes officiels pour aider les dirigeants de PME."
        path="/ressources"
      />
      <PageHero
        label="Ressources"
        title="Des repères utiles, publiés progressivement"
        intro="Cet espace réunira au fil du temps des ressources pédagogiques destinées à aider les dirigeants de PME à comprendre et à préparer les transformations. Les contenus sont ajoutés progressivement, sans précipitation."
        testid="ressources-hero"
      />

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24" data-testid="ressources-types">
        <Reveal>
          <span className="bz-label text-bz-petrol">Ce que vous y trouverez</span>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden border border-bz-line bg-bz-line md:grid-cols-2 lg:grid-cols-3">
          {resourceTypes.map((r, i) => (
            <Reveal key={r.title} delay={(i % 3) * 0.08} className="bg-white">
              <div className="flex h-full flex-col p-10" data-testid={`ressource-card-${i}`}>
                <r.icon size={28} strokeWidth={1.2} className="text-bz-petrol" />
                <h3 className="mt-6 font-serif text-xl text-bz-navy">{r.title}</h3>
                <p className="mt-4 flex-1 text-base leading-relaxed text-bz-slate">{r.text}</p>
                <span className="mt-6 inline-flex w-fit border border-bz-line px-3 py-1 text-[0.65rem] uppercase tracking-[0.12em] text-bz-slate">
                  {r.state}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-bz-line bg-bz-ivory2" data-testid="ressources-officials">
        <div className="mx-auto max-w-4xl px-6 py-20 lg:py-24">
          <Reveal>
            <span className="bz-label text-bz-petrol">Organismes officiels</span>
            <h2 className="mt-6 font-serif text-3xl leading-tight text-bz-navy lg:text-4xl">
              Aller directement aux sources
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-bz-slate">
              BENZENATI privilégie les sources officielles et reconnues. Voici quelques points d'entrée fiables pour approfondir par vous-même.
            </p>
            <ul className="mt-10 divide-y divide-bz-line border-y border-bz-line">
              {officialLinks.map((l, i) => (
                <li key={l.url}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`official-link-${i}`}
                    className="group flex items-center justify-between gap-4 py-5 text-base text-bz-navy transition-colors hover:text-bz-petrol"
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight size={18} strokeWidth={1.5} className="shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm leading-relaxed text-bz-slate/80">
              Les liens ci-dessus renvoient vers des sites externes officiels. BENZENATI ne publie aucun document téléchargeable tant que celui-ci n'a pas été rédigé et vérifié.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
